import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { usePage } from "@/contexts/PageContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OrderQuantitySelector } from "./checkout/OrderQuantitySelector";
import { getContrastTextColor } from "@/utils/colorUtils";
import { formatCentsAsUsd } from "@/lib/money";
import { toast } from "sonner";

interface DisplayProduct {
  _id: string;
  name?: string;
  variantName?: string;
  price: number;
  images?: { url: string; thumbnailUrl?: string }[];
  payWhatYouCan?: boolean;
  quantityRules?: { minimum: number; maximum: number };
}

export function OrderReceiptRoute() {
  const { id: orderId } = useParams<{ id: string }>();
  const { setCheckoutSettings } = usePage();
  const [artistName, setArtistName] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [products, setProducts] = useState<DisplayProduct[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [receiptTip, setReceiptTip] = useState(false);
  const [checkoutSettings, setLocalCheckoutSettings] = useState<{
    colors?: {
      backgroundColor?: string;
      buttonColor?: string;
      accentColor?: string;
    };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const noopQuantity = useCallback(() => {}, []);

  useEffect(() => {
    if (!orderId) return;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setReceiptTip(false);
      try {
        const res = await fetch(`/api/orders/${orderId}`);
        if (!res.ok) {
          if (res.status === 404) setNotFound(true);
          return;
        }
        const data = (await res.json()) as {
          artistName?: string;
          payment?: { status?: string; amount?: number };
          products?: DisplayProduct[];
          quantities?: Record<string, number>;
          receipt?: { tip?: boolean };
          checkoutSettings?: {
            colors?: {
              backgroundColor?: string;
              buttonColor?: string;
              accentColor?: string;
            };
          } | null;
        };
        setArtistName(data.artistName ?? "");
        setPaymentStatus(data.payment?.status);
        setPaymentAmount(data.payment?.amount ?? 0);
        setProducts(data.products ?? []);
        setQuantities(data.quantities ?? {});
        setReceiptTip(data.receipt?.tip === true);
        const cs = data.checkoutSettings ?? null;
        setLocalCheckoutSettings(cs);
        if (cs) setCheckoutSettings(cs);
      } catch (e) {
        console.error("Order receipt fetch error:", e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [orderId, setCheckoutSettings]);

  const backgroundColor = checkoutSettings?.colors?.backgroundColor ?? "#ffffff";
  const accentColor = checkoutSettings?.colors?.accentColor ?? "#000000";
  const textColor = getContrastTextColor(backgroundColor);
  const accentTextColor = getContrastTextColor(accentColor);

  const receiptTitle = (() => {
    if (!orderId) return "Order";
    if (notFound) return "Order not found";
    const titleBase = artistName.trim() ? artistName.trim() : "Receipt";
    return paymentStatus === "complete"
      ? `Receipt · ${titleBase}`
      : `Order · ${titleBase}`;
  })();

  const handleCancel = async () => {
    if (!orderId) return;
    setCancelError(null);
    setCancelling(true);
    try {
      const res = await fetch(`/api/payments/${orderId}`, { method: "POST" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCancelError(typeof body.error === "string" ? body.error : "Cancel failed");
        return;
      }
      setPaymentStatus("cancelled");
      setCancelDialogOpen(false);
      toast.success("Your order has been cancelled.");
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  if (!orderId) {
    return (
      <>
        <Helmet>
          <title>{receiptTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center" style={{ color: textColor }}>
          Missing order ID
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{receiptTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor }}>
          <div style={{ color: textColor }}>Loading...</div>
        </div>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <Helmet>
          <title>{receiptTitle}</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor }}>
          <div style={{ color: textColor }}>Order not found</div>
        </div>
      </>
    );
  }

  const hasTipLine = products.some((p) => (p.name ?? "").trim().toLowerCase() === "tip");

  return (
    <>
      <Helmet>
        <title>{receiptTitle}</title>
      </Helmet>
      <div className="min-h-screen w-full" style={{ backgroundColor }}>
      <div
        className="max-w-4xl w-full mx-auto min-h-screen flex flex-col py-4 px-4"
        style={{ color: textColor }}
      >
        <h1 className="text-2xl font-bold text-center">{artistName}</h1>

        <div className="flex justify-center py-4">
          <span
            className="inline-flex h-8 shrink-0 items-center justify-center rounded-full px-5 text-sm font-medium"
            style={{ backgroundColor: accentColor, color: accentTextColor }}
          >
            Receipt
          </span>
        </div>

        <div className="flex-1 mt-2 space-y-6">
          {products.length > 0 ? (
            <OrderQuantitySelector
              products={products}
              quantities={quantities}
              onQuantityChange={noopQuantity}
              total={paymentAmount}
              readOnly
              checkoutColors={checkoutSettings?.colors ?? null}
              showShippingLineItem={false}
              showShippingProtectionLineItem={false}
              tipAmount={0}
            />
          ) : (
            <div className="text-center text-sm opacity-80 py-8 space-y-2">
              <p>No line items recorded for this order.</p>
              {paymentAmount > 0 ? (
                <p className="font-semibold text-lg">Total: ${formatCentsAsUsd(paymentAmount)}</p>
              ) : null}
            </div>
          )}

          {receiptTip && !hasTipLine ? (
            <p className="text-sm text-center opacity-80">Thank you — this order included a tip.</p>
          ) : null}

          {paymentStatus === "uncaptured" ? (
            <>
              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
                  disabled={cancelling}
                  onClick={() => {
                    setCancelError(null);
                    setCancelDialogOpen(true);
                  }}
                >
                  Cancel
                </Button>
              </div>
              <Dialog
                open={cancelDialogOpen}
                onOpenChange={(open) => {
                  if (cancelling) return;
                  setCancelDialogOpen(open);
                  if (!open) setCancelError(null);
                }}
              >
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Cancel this order?</DialogTitle>
                    <DialogDescription>
                      Your held payment will be released and this order will not be completed. You can
                      place a new order later if you change your mind.
                    </DialogDescription>
                  </DialogHeader>
                  {cancelError ? (
                    <p className="text-sm text-red-600 dark:text-red-400">{cancelError}</p>
                  ) : null}
                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={cancelling}
                      onClick={() => setCancelDialogOpen(false)}
                    >
                      Keep order
                    </Button>
                    <Button
                      type="button"
                      className="bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
                      disabled={cancelling}
                      onClick={() => void handleCancel()}
                    >
                      {cancelling ? "Cancelling..." : "Yes, cancel order"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          ) : null}
        </div>
      </div>
    </div>
    </>
  );
}
